import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useChainId } from 'wagmi'
import { formatEther } from 'viem'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { RoleGate } from '@/components/auth/RoleGate'
import { ReferralAccessBanners } from '@/components/referrals/ReferralAccessBanners'
import { RegisterReferrerCard } from '@/components/referrals/RegisterReferrerCard'
import { ReferrerRoster } from '@/components/referrals/ReferrerRoster'
import type { ReferrerActionKind } from '@/components/referrals/ReferrerRosterRow'
import { useReferralRoster, type ReferrerRow } from '@/hooks/chain/useReferralRoster'
import { useReferralConfig } from '@/hooks/chain/useReferralConfig'
import { useReferralAdminMutations } from '@/hooks/chain/useReferralAdminMutations'
import { useReferralAccess } from '@/hooks/chain/useReferralAccess'
import { useReferralRegistrationPreflight } from '@/hooks/chain/useReferralRegistrationPreflight'
import { getContractAddress } from '@/config/contracts'
import { chainName } from '@/config/chains'
import { isHexAddress } from '@/lib/utils'

export const Route = createFileRoute('/referrals')({
    component: ReferralsPage,
})

// Canonical code constraint mirrored from ReferralManager._normalizeCode: 3..32 of [A-Za-z0-9_].
const CODE_RE = /^[A-Za-z0-9_]{3,32}$/

type PendingAction = { kind: ReferrerActionKind; row: ReferrerRow }

export function ReferralsPage() {
    const chainId = useChainId()
    const manager = getContractAddress(chainId, 'referralManager')
    const { referrers, isLoading, error: rosterError, refetch: refetchRoster } = useReferralRoster()
    const { defaultShareBps, maxShareBps } = useReferralConfig()
    const mutations = useReferralAdminMutations()
    const access = useReferralAccess()
    const busy = mutations.isPending || mutations.isConfirming
    // Preflight verdicts stay silent while loading: a warning must assert a confirmed
    // on-chain state, never a read that hasn't resolved yet.
    const missingOperatorRole = !access.isLoading && !access.isOperator
    const missingTreasuryRole = !access.isLoading && !access.isTreasury
    const unwired = !access.isLoading && !access.wired

    const [pending, setPending] = useState<PendingAction | null>(null)

    // Registration form state; the controlled card owns the markup.
    const [regAddress, setRegAddress] = useState('')
    const [regCode, setRegCode] = useState('')
    const [regOverride, setRegOverride] = useState('')
    const [attestKyc, setAttestKyc] = useState(false)
    const [attestGeo, setAttestGeo] = useState(false)
    const [attestTerms, setAttestTerms] = useState(false)

    const [rateDraft, setRateDraft] = useState('')

    // Debounced pre-submit conflict checks: a registered address or taken code
    // errors inline instead of reverting on-chain. Only a confirmed conflict blocks the
    // submit — an in-flight check never does (the contract revert stays the backstop).
    const preflight = useReferralRegistrationPreflight(regAddress, regCode)

    if (!manager) {
        return (
            <div className="space-y-2">
                <h1 className="text-2xl font-semibold">Referrals</h1>
                <p className="text-muted-foreground text-sm">
                    The ReferralManager is not deployed on {chainName(chainId)}. Deploy and sync
                    addresses (<code className="font-mono text-xs">bun sync-addresses</code>) to
                    administer the referral program here.
                </p>
            </div>
        )
    }

    const addrValid = isHexAddress(regAddress.trim())
    const codeValid = CODE_RE.test(regCode.trim())
    const overrideBps = regOverride.trim() === '' ? 0 : Number(regOverride)
    const overrideValid =
        Number.isInteger(overrideBps) && overrideBps >= 0 && overrideBps <= maxShareBps
    const canRegister =
        addrValid &&
        codeValid &&
        overrideValid &&
        attestKyc &&
        attestGeo &&
        attestTerms &&
        !busy &&
        !missingOperatorRole &&
        !preflight.addressConflict &&
        !preflight.codeConflict

    function onRegister() {
        if (!canRegister) return
        mutations.register(regAddress.trim() as `0x${string}`, regCode.trim(), overrideBps)
        setRegAddress('')
        setRegCode('')
        setRegOverride('')
        setAttestKyc(false)
        setAttestGeo(false)
        setAttestTerms(false)
    }

    const rateValue = rateDraft.trim() === '' ? undefined : Number(rateDraft)
    const rateValid =
        rateValue !== undefined &&
        Number.isInteger(rateValue) &&
        rateValue >= 0 &&
        rateValue <= maxShareBps

    function onConfirmPending() {
        if (!pending) return
        const target = pending.row.address
        if (pending.kind === 'suspend') mutations.suspend(target)
        if (pending.kind === 'reactivate') mutations.reactivate(target)
        if (pending.kind === 'terminate') mutations.terminate(target)
        if (pending.kind === 'clawback') mutations.clawback(target)
        setPending(null)
    }

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">Referrals</h1>
                <p className="text-muted-foreground text-sm">
                    On-chain referral program administration for {chainName(chainId)}. Registration,
                    the per-referrer off-switch, and clawback are{' '}
                    <code className="font-mono text-xs">ReferralManager</code> transactions signed
                    by your wallet, which must hold the corresponding on-chain role.
                </p>
            </div>

            <ReferralAccessBanners
                unwired={unwired}
                missingOperatorRole={missingOperatorRole}
                canWire={access.isLgoAdmin}
                canGrant={Boolean(access.isRmAdmin && access.account)}
                busy={busy}
                managerAddress={manager}
                onWire={() => mutations.wireReferralManager()}
                onGrant={() => access.account && mutations.grantOperatorRole(access.account)}
            />

            <RoleGate roles={['admin']}>
                <RegisterReferrerCard
                    address={regAddress}
                    code={regCode}
                    override={regOverride}
                    attestKyc={attestKyc}
                    attestGeo={attestGeo}
                    attestTerms={attestTerms}
                    defaultShareBps={defaultShareBps}
                    addressConflict={preflight.addressConflict}
                    codeConflict={preflight.codeConflict}
                    canRegister={canRegister}
                    onAddressChange={setRegAddress}
                    onCodeChange={setRegCode}
                    onOverrideChange={setRegOverride}
                    onAttestKycChange={setAttestKyc}
                    onAttestGeoChange={setAttestGeo}
                    onAttestTermsChange={setAttestTerms}
                    onRegister={onRegister}
                />
            </RoleGate>

            <ReferrerRoster
                referrers={referrers}
                isLoading={isLoading}
                error={rosterError}
                actionsDisabled={busy || missingOperatorRole}
                onAction={(kind, row) => setPending({ kind, row })}
                onRetry={() => void refetchRoster()}
            />

            <RoleGate roles={['admin']}>
                <Card>
                    <CardHeader>
                        <CardTitle>Rates</CardTitle>
                        <CardDescription>
                            Default referral share in basis points of the operator fee. Hard cap
                            {` ${maxShareBps}`} bps on-chain; changes apply to future accruals only.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="flex items-end gap-3">
                        <div className="space-y-1">
                            <Label htmlFor="rate-default">Default share (bps)</Label>
                            <Input
                                id="rate-default"
                                placeholder={String(defaultShareBps ?? '')}
                                value={rateDraft}
                                onChange={(e) => setRateDraft(e.target.value)}
                                className="w-40"
                            />
                        </div>
                        <Button
                            disabled={!rateValid || busy || missingTreasuryRole}
                            onClick={() => rateValid && mutations.setDefaultShareBps(rateValue)}
                        >
                            Save rate
                        </Button>
                        {missingTreasuryRole && (
                            <p className="text-muted-foreground text-xs">
                                Your wallet does not hold the ReferralManager TREASURY_ROLE, which
                                gates rate changes.
                            </p>
                        )}
                    </CardContent>
                </Card>
            </RoleGate>

            <Dialog open={pending !== null} onOpenChange={(open) => !open && setPending(null)}>
                <DialogContent>
                    {pending?.kind === 'suspend' && (
                        <>
                            <DialogHeader>
                                <DialogTitle>Suspend {pending.row.code}?</DialogTitle>
                                <DialogDescription>
                                    Suspension pauses accrual and claims on-chain until reactivated.
                                    Entries by their players continue, with the full operator fee
                                    routing to the treasury; missed accrual is never back-paid.
                                </DialogDescription>
                            </DialogHeader>
                            <DialogFooter>
                                <Button variant="destructive" onClick={onConfirmPending}>
                                    Confirm suspension
                                </Button>
                            </DialogFooter>
                        </>
                    )}
                    {pending?.kind === 'reactivate' && (
                        <>
                            <DialogHeader>
                                <DialogTitle>Reactivate {pending.row.code}?</DialogTitle>
                                <DialogDescription>
                                    Restores accrual and claims from the next entry onward.
                                </DialogDescription>
                            </DialogHeader>
                            <DialogFooter>
                                <Button onClick={onConfirmPending}>Confirm reactivation</Button>
                            </DialogFooter>
                        </>
                    )}
                    {pending?.kind === 'terminate' && (
                        <>
                            <DialogHeader>
                                <DialogTitle>Terminate {pending.row.code}?</DialogTitle>
                                <DialogDescription>
                                    Termination is permanent: accrual and claims halt forever, and
                                    the unclaimed escrow becomes clawback-eligible. This cannot be
                                    reversed on-chain.
                                </DialogDescription>
                            </DialogHeader>
                            <DialogFooter>
                                <Button variant="destructive" onClick={onConfirmPending}>
                                    Confirm termination
                                </Button>
                            </DialogFooter>
                        </>
                    )}
                    {pending?.kind === 'clawback' && (
                        <>
                            <DialogHeader>
                                <DialogTitle>
                                    Claw back {pending.row.code}&apos;s escrow?
                                </DialogTitle>
                                <DialogDescription>
                                    Sweeps {formatEther(pending.row.accrued)} ETH of unclaimed
                                    rewards to the operator treasury. Only possible because this
                                    referrer is terminated.
                                </DialogDescription>
                            </DialogHeader>
                            <DialogFooter>
                                <Button variant="destructive" onClick={onConfirmPending}>
                                    Confirm clawback
                                </Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    )
}
